import {
  CLONE_TIMEOUT_MS,
  DEFAULT_RETRY_DELAY_MS,
  FETCH_TIMEOUT_MS,
  GIT_PREP_MAX_RETRIES,
} from "../boot/git-prep.js";
import { getWorkbenchClient } from "../workbench/client.js";
import { WorkbenchError } from "../workbench/errors.js";
import { workbenchEnabled } from "../workbench/mode.js";
import type { GitStatusFrame } from "../workbench/protocol.js";

/**
 * Result of the agent-side git-readiness gate.
 *
 * - `"ready"`     — the workbench daemon's `GitPrepJob` reported `ready`; the
 *                   task repo is up to date.
 * - `"failed"`    — the daemon reported `failed` (all retries exhausted). The
 *                   caller must NOT operate on the repo.
 * - `"timeout"`   — the daemon never reported `ready`/`failed` before the
 *                   deadline, or the wait was aborted.
 * - `"not-gated"` — the workbench split is not enabled for this pod (GitHub
 *                   Codespaces, local dev, or the workbench container itself),
 *                   so there is no daemon to gate on.
 */
export type GitReadyState = "ready" | "failed" | "timeout" | "not-gated";

export interface AwaitGitReadyOptions {
  /** Overall deadline. See DEFAULT_TIMEOUT_MS for why this defaults so high. */
  timeoutMs?: number;
  /** Poll interval. Defaults to 200ms. */
  pollMs?: number;
  /** Optional log sink for progress messages. */
  onLog?: (msg: string) => void;
  /** Optional shutdown signal. Aborting resolves promptly with "timeout"
   *  rather than rejecting — callers (session/supervisor shutdown paths)
   *  treat an aborted wait as just another way of not becoming ready. */
  signal?: AbortSignal;
  /** Test seam; defaults to getWorkbenchClient(). */
  clientFn?: () => { gitStatus(): Promise<GitStatusFrame> };
}

/** Extra headroom past the daemon's theoretical worst case. */
const GATE_MARGIN_MS = 5 * 60_000;

/**
 * DERIVED from the daemon's retry envelope, not hand-picked: `GitPrepJob`
 * can legitimately stay "pending" through `GIT_PREP_MAX_RETRIES` attempts,
 * each bounded by a clone timeout plus a fetch timeout, with a retry delay
 * between attempts — before it either reports "ready" or gives up and
 * reports "failed". Timing out this gate before that envelope elapses would
 * misreport a slow-but-recovering prep (e.g. a hung clone the daemon's own
 * retry loop is still working through) as a failure — exactly the misreport
 * this gate exists to prevent. Deriving the ceiling means a future change to
 * the git-prep timeouts automatically moves the gate with it.
 * (= 3_020_000 ms ≈ 50 min with today's numbers.)
 */
export const DEFAULT_TIMEOUT_MS =
  GIT_PREP_MAX_RETRIES * (CLONE_TIMEOUT_MS + FETCH_TIMEOUT_MS) +
  (GIT_PREP_MAX_RETRIES - 1) * DEFAULT_RETRY_DELAY_MS +
  GATE_MARGIN_MS;
const DEFAULT_POLL_MS = 200;

/**
 * Block until the workbench daemon's background git preparation reports
 * ready or failed over the `gitStatus` loopback op — the agent-side gate:
 * Claude must not spawn on a stale branch, and setup/start scripts need the
 * repo present.
 *
 * When the workbench split is not enabled for this pod (GitHub Codespaces,
 * local dev, or the workbench container itself), returns `"not-gated"`
 * immediately — those environments have no daemon-owned background git prep
 * to wait on. The agent's own authoritative checkout (session-runner phase
 * 3.5b) runs unconditionally afterward regardless of which state resolved.
 *
 * Never throws and always resolves to a `GitReadyState` — an explicit
 * shutdown abort resolves promptly with `"timeout"` instead of rejecting.
 */
export function awaitGitReady(opts: AwaitGitReadyOptions = {}): Promise<GitReadyState> {
  if (!workbenchEnabled()) {
    return Promise.resolve("not-gated");
  }
  const clientFn = opts.clientFn ?? getWorkbenchClient;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const pollMs = opts.pollMs ?? DEFAULT_POLL_MS;

  opts.onLog?.("waiting for workspace git (workbench daemon)");
  return pollDaemon(clientFn, timeoutMs, pollMs, opts.onLog, opts.signal);
}

/** Resolves after `ms`, or early (without rejecting) if `signal` aborts —
 *  the poll loop's own abort check on the next iteration is what turns an
 *  early wake into a prompt "timeout" resolution. */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  if (!signal) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      resolve();
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * One `gitStatus` poll. A null `state` means "keep polling"; a non-null one is
 * terminal. `log`, when present, is the message the caller should surface.
 */
interface PollOutcome {
  state: GitReadyState | null;
  log?: string;
}

/** `reportError` gates the transient-error log to the FIRST occurrence — a
 *  daemon mid-restart must not spam the pod log every poll interval. */
async function pollOnce(
  clientFn: () => { gitStatus(): Promise<GitStatusFrame> },
  reportError: boolean,
): Promise<PollOutcome> {
  try {
    const frame = await clientFn().gitStatus();
    if (frame.state === "ready") return { state: "ready", log: "workspace git ready" };
    if (frame.state === "failed") {
      return {
        state: "failed",
        log: `workspace git preparation failed: ${frame.reason ?? "unknown reason"}`,
      };
    }
    // "pending" — the daemon is still working; poll again.
    return { state: null };
  } catch (err) {
    // The daemon rejected us outright: a missing/invalid workbench token is
    // deterministic, so every remaining poll would be rejected identically.
    // Retrying it is what turned a token regression into a silent ~50-minute
    // wedge — fail fast and loud instead.
    if (err instanceof WorkbenchError && err.code === "unauthorized") {
      return {
        state: "failed",
        log: "workspace git gate unauthorized — workbench token missing or invalid, giving up",
      };
    }
    // Anything else is potentially transient (daemon mid-restart), so keep
    // polling — but leave a trace in the pod log instead of failing silently.
    const message = err instanceof Error ? err.message : String(err);
    return reportError
      ? { state: null, log: `workspace git poll error (retrying): ${message}` }
      : { state: null };
  }
}

async function pollDaemon(
  clientFn: () => { gitStatus(): Promise<GitStatusFrame> },
  timeoutMs: number,
  pollMs: number,
  onLog?: (msg: string) => void,
  signal?: AbortSignal,
): Promise<GitReadyState> {
  const deadline = Date.now() + timeoutMs;
  let loggedError = false;
  for (;;) {
    if (signal?.aborted) {
      onLog?.("workspace git wait aborted — giving up");
      return "timeout";
    }
    const outcome = await pollOnce(clientFn, !loggedError);
    if (outcome.log !== undefined) {
      loggedError = true;
      onLog?.(outcome.log);
    }
    if (outcome.state !== null) return outcome.state;
    if (Date.now() >= deadline) {
      onLog?.(`workspace git not ready after ${timeoutMs}ms — giving up`);
      return "timeout";
    }
    await delay(pollMs, signal);
  }
}
